import { LoadingIndicator } from 'components/LoadingIndicator';
import NotAvailable from 'components/NotAvailable';
import { MainProvider } from 'context/main/provider';
import { MainInteractiveCtaTrackingProvider } from 'context/interactiveCtaTracking/provider';
import GlobalStyle from 'globalStyles';
import { useFontFamilyOptions } from 'hooks';
import { PageLocation, PageSection } from 'hooks/GTM/types';
import { useGTMInject } from 'hooks/GTM/useGTMInject';
import { useGTMTrackPageView } from 'hooks/GTM/useGTMTrackPageView';
import i18n from 'i18n';
import { usePlaybackDetailsQuery } from 'queries/details/usePlaybackDetailsQuery';
import { usePreviewQuery } from 'queries/preview/usePreviewQuery';
import { Suspense, lazy, useEffect, useState } from 'react';
import { CovideoThemeProvider } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { isCdkEnvironment } from 'utils/constants';
import { SHOWROOM_TEMPLATE_ID } from 'pages/showroom/types';
import { ReactionProvider } from './context/reactions/ReactionProvider';
import { SocketProvider } from './context/socket/SocketProvider';

const LandingPage = lazy(() =>
  import('pages/landing').then(m => ({ default: m.LandingPage }))
);
const PlaybackPage = lazy(() =>
  import('pages/playback/Playback').then(m => ({ default: m.PlaybackPage }))
);
const LandingPagePreview = lazy(() =>
  import('pages/preview/LandingPagePreview').then(m => ({
    default: m.LandingPagePreview,
  }))
);
const ShowroomLinkPage = lazy(() =>
  import('pages/showroom').then(m => ({ default: m.ShowroomLinkPage }))
);
const ShowroomPreviewPage = lazy(() =>
  import('pages/showroom').then(m => ({ default: m.ShowroomPreviewPage }))
);
const ShowroomSignedPreviewPage = lazy(() =>
  import('pages/showroom').then(m => ({
    default: m.ShowroomSignedPreviewPage,
  }))
);
const VideoReplyPage = lazy(() =>
  import('./pages/VideoReply').then(m => ({ default: m.VideoReplyPage }))
);

type Props = { setLanguage: (lang: string) => void };
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

const DefaultPath = () => {
  window.location.href = 'https://www.covideo.com';
  return <></>;
};

const ShortUrlPages = (props: Props) => {
  const { setLanguage } = props;
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePlaybackDetailsQuery();
  const fontFamilyOptions = useFontFamilyOptions();
  const { trackVideoSpecificPagesManually } = useGTMTrackPageView();

  useEffect(() => {
    if (data) {
      const { video, ctaSet, landingPageDesign } = data;
      trackVideoSpecificPagesManually?.({
        inventoryItem: data?.inventoryItem,
        pageLocation: PageLocation.LANDING_PAGE,
        pageSection: PageSection.OVERVIEW,
        videoAttributes: {
          videoId: video?.id ? Number(video?.id) : undefined,
          videoLength: video?.videoLength,
          videoCustomerName: !!video?.customerName,
          videoType: video?.videoType || undefined,
          videoTitle: video?.title || undefined,
        },
        videoMessageProperties: {
          callToAction: ctaSet?.title || undefined,
          landingPage: landingPageDesign?.title || undefined,
          notificationViewed: data?.notifyUser || false,
        },
        userData: {
          cdkEnterpriseId: data?.cdkEnterpriseId || undefined,
          cdkStoreId: data?.cdkStoreId || undefined,
        },
      });
      setLanguage(data.language || 'en');
      localStorage.setItem('language', data.language || 'en');
    }
  }, [data, trackVideoSpecificPagesManually]);

  if (isLoading) {
    return <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />;
  }

  if (isError || !data) {
    return <NotAvailable />;
  }

  if (data.redirectShortUrl) {
    window.location.href = data.redirectShortUrl;
    return <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />;
  }

  const pageFallback = (
    <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />
  );

  if (data.manifest) {
    return (
      <Suspense fallback={pageFallback}>
        <ShowroomLinkPage details={data} manifest={data.manifest} />
      </Suspense>
    );
  }

  return data.landingPageDesign ? (
    <MainProvider value={data}>
      <GlobalStyle
        landingPageDesign={data?.landingPageDesign || {}}
        fonts={fontFamilyOptions}
      />
      <MainInteractiveCtaTrackingProvider>
        <Suspense fallback={pageFallback}>
          <LandingPage />
        </Suspense>
      </MainInteractiveCtaTrackingProvider>
    </MainProvider>
  ) : (
    <Suspense fallback={pageFallback}>
      <PlaybackPage />
    </Suspense>
  );
};

const PreviewPages = (props: Props) => {
  const { setLanguage } = props;
  const { t } = useTranslation();
  const { data, isError, isLoading } = usePreviewQuery();
  const { trackVideoSpecificPagesManually } = useGTMTrackPageView();
  useEffect(() => {
    if (data) {
      const { ctaSet, landingPageDesign } = data;
      trackVideoSpecificPagesManually?.({
        inventoryItem: data?.inventoryItem,
        pageLocation: PageLocation.LANDING_PAGE,
        pageSection: PageSection.PREVIEW,
        // preview does not have full video attributes like playback page
        videoAttributes: {},
        videoMessageProperties: {
          callToAction: ctaSet?.title || undefined,
          landingPage: landingPageDesign?.title || undefined,
          notificationViewed: data?.notifyUser || false,
        },
        userData: {
          cdkEnterpriseId: data?.cdkEnterpriseId || undefined,
          cdkStoreId: data?.cdkStoreId || undefined,
        },
      });
      setLanguage(data.language || 'en');
      localStorage.setItem('language', data.language || 'en');
    }
  }, [data, trackVideoSpecificPagesManually]);

  const fontFamilyOptions = useFontFamilyOptions();

  if (isLoading) {
    return <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />;
  }

  if (isError || !data) {
    return <NotAvailable />;
  }

  const pageFallback = (
    <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />
  );

  return (
    <MainProvider value={data}>
      <GlobalStyle
        landingPageDesign={data?.landingPageDesign || {}}
        fonts={fontFamilyOptions}
      />
      <Suspense fallback={pageFallback}>
        <LandingPagePreview />
      </Suspense>
    </MainProvider>
  );
};

// Showroom links preview from the manifest alone; other designs keep the
// existing builder preview.
const PreviewRoute = (props: Props) => {
  const { landingPageDesignId } = useParams();
  const { t } = useTranslation();
  if (Number(landingPageDesignId) === SHOWROOM_TEMPLATE_ID) {
    return (
      <Suspense
        fallback={
          <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />
        }
      >
        <ShowroomPreviewPage setLanguage={props.setLanguage} />
      </Suspense>
    );
  }
  return <PreviewPages {...props} />;
};

const AppLayout = () => {
  return (
    <CovideoThemeProvider themeName={isCdkEnvironment ? 'cdk' : 'branding'}>
      <SocketProvider>
        <QueryClientProvider client={queryClient}>
          <ReactionProvider>
            <Outlet />
          </ReactionProvider>
        </QueryClientProvider>
        <ToastContainer style={{ minWidth: 492 }} />
      </SocketProvider>
    </CovideoThemeProvider>
  );
};

export const AppRoutesLayout = () => {
  return (
    <Suspense
      fallback={<LoadingIndicator isLoading={true} height='100vh' text='' />}
    >
      <Outlet />
    </Suspense>
  );
};

type AppRouterProps = {
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
};
export const useAppRouter = ({ setLanguage }: AppRouterProps) => {
  const { t } = useTranslation();

  return createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={<AppLayout />}
        errorElement={<NotAvailable />}
        hydrateFallbackElement={
          <LoadingIndicator isLoading={true} height='100vh' text='' />
        }
      >
        <Route element={<AppRoutesLayout />}>
          <Route path='/' element={<DefaultPath />} />
          <Route
            path='/:shortUrl'
            element={<ShortUrlPages setLanguage={setLanguage} />}
          />
          <Route
            path='/:shortUrl/video-reply/:eridEncoded'
            element={
              <Suspense
                fallback={
                  <LoadingIndicator
                    isLoading
                    text={t('Loading...')}
                    height='100vh'
                  />
                }
              >
                <VideoReplyPage />
              </Suspense>
            }
          />
          <Route
            path='/preview/showroom'
            element={
              <Suspense
                fallback={
                  <LoadingIndicator
                    isLoading
                    text={t('Loading...')}
                    height='100vh'
                  />
                }
              >
                <ShowroomSignedPreviewPage setLanguage={setLanguage} />
              </Suspense>
            }
          />
          <Route
            path='/preview/:landingPageDesignId'
            element={<PreviewPages setLanguage={setLanguage} />}
          />
          <Route
            path='/preview/:landingPageDesignId/:shortUrl'
            element={<PreviewRoute setLanguage={setLanguage} />}
          />
        </Route>
      </Route>
    ),
    { basename: '' }
  );
};

export const AppRouterRoot = () => {
  const [language, setLanguage] = useState<string>('en');
  const { isLoadingScript } = useGTMInject(isCdkEnvironment);
  const { t } = useTranslation();
  const router = useAppRouter({ setLanguage });

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  if (isLoadingScript) {
    return <LoadingIndicator isLoading text={t('Loading...')} height='100vh' />;
  }
  return <RouterProvider router={router} />;
};
